import { supabase } from '../lib/supabase';
import { calculateSecurityScore } from '../utils/securityCalculator';
import { generateRecommendedActions } from './recommendationService';
import { bulkCreateActionsFromRecommendations, createInitialSnapshot } from './progressTrackingService';
import { DiagnosticFormData } from '../types/diagnostic';

export async function ensureActionsExist(diagnosticId: string): Promise<boolean> {
  try {
    const { data: existingActions, error: actionsError } = await supabase
      .from('improvement_actions_history')
      .select('id')
      .eq('diagnostic_id', diagnosticId)
      .limit(1);

    if (actionsError) {
      console.error('Error checking actions:', actionsError);
      return false;
    }

    if (existingActions && existingActions.length > 0) {
      return true;
    }

    const { data: diagnostic, error: diagError } = await supabase
      .from('diagnostics')
      .select('*')
      .eq('id', diagnosticId)
      .single();

    if (diagError || !diagnostic) {
      console.error('Error fetching diagnostic:', diagError);
      return false;
    }

    const formData = diagnostic.diagnostic_data;
    if (!formData) {
      console.error('No form data found in diagnostic');
      return false;
    }

    const scoreData = calculateSecurityScore(formData as DiagnosticFormData);
    const recommendedActions = generateRecommendedActions(
      formData as DiagnosticFormData,
      scoreData.categoryScores
    );

    const { data: snapshotCheck } = await supabase
      .from('score_snapshots')
      .select('id')
      .eq('diagnostic_id', diagnosticId)
      .limit(1);

    if (!snapshotCheck || snapshotCheck.length === 0) {
      await createInitialSnapshot(
        diagnosticId,
        diagnostic.security_score,
        scoreData.categoryScores.protection,
        scoreData.categoryScores.surveillance,
        scoreData.categoryScores.deterrence
      );
    }

    await bulkCreateActionsFromRecommendations(
      diagnosticId,
      recommendedActions.map(action => ({
        id: action.id,
        title: action.title,
        category: action.category as 'protection' | 'surveillance' | 'dissuasion',
        priority: action.priority as 'high' | 'medium' | 'low',
        estimatedCost: action.estimatedCost,
        scoreImpact: action.scoreImpact,
        description: action.description
      }))
    );

    return true;
  } catch (error) {
    console.error('Error in ensureActionsExist:', error);
    return false;
  }
}

export async function repairAllDiagnosticsWithoutActions(): Promise<{
  total: number;
  repaired: number;
  failed: number;
}> {
  try {
    const { data: diagnostics, error } = await supabase
      .from('diagnostics')
      .select('id')
      .order('created_at', { ascending: false });

    if (error || !diagnostics) {
      console.error('Error fetching diagnostics:', error);
      return { total: 0, repaired: 0, failed: 0 };
    }

    let repaired = 0;
    let failed = 0;

    for (const diagnostic of diagnostics) {
      const success = await ensureActionsExist(diagnostic.id);
      if (success) {
        repaired++;
      } else {
        failed++;
      }
    }

    return {
      total: diagnostics.length,
      repaired,
      failed
    };
  } catch (error) {
    console.error('Error in repairAllDiagnosticsWithoutActions:', error);
    return { total: 0, repaired: 0, failed: 0 };
  }
}
